import { useIntl } from 'react-intl';

import {
  buildSharedTooltipHeader,
  buildSharedTooltipRow,
} from './sharedXYChartTooltipUtils.js';
import { OverlayTooltip } from '../../../../core/components/overlay-tooltip/OverlayTooltip.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type { SeriesQueryResult } from '../../../context/XYChartOverlayPerformance.context.js';
import { useAxesFormatter } from '../../../hooks/useAxesFormatter.js';
import { useBarData } from '../../../hooks/useBarData.js';
import { useFormatCategory } from '../../../hooks/useFormatCategory.js';
import { useXYChartColorScales } from '../../../hooks/useXYChartColorScales.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartTruncationMode } from '../../../hooks/useXYChartTruncationMode.js';
import type { TooltipContent } from '../../../types/state.js';

type SharedTooltipCtx = Parameters<typeof buildSharedTooltipRow>[1];

type LazySharedRow = {
  name: string;
  result: SeriesQueryResult;
};

function LazySharedTooltipRow({
  raw,
  ctx,
}: {
  raw: LazySharedRow;
  ctx: SharedTooltipCtx;
}) {
  const row = buildSharedTooltipRow(raw.result as TooltipContent, ctx);
  const value = row.yAxisDuration
    ? `${row.yAxisValue} (${row.yAxisDuration})`
    : row.yAxisValue;

  return (
    <OverlayTooltip.SeriesItem
      name={row.name}
      value={value}
      color={row.color}
      actions={row.actions}
    />
  );
}

export function SharedXYChartTooltip({
  preferred,
  datapoints,
}: Readonly<{
  preferred: TooltipContent;
  datapoints: SeriesQueryResult[];
}>) {
  const { seriesToMetadata, axisIdToScaleDomain, datapointMetadataMap } =
    useXYChartData();
  const axisIdToFormatter = useAxesFormatter();
  const intl = useIntl();
  const colorScaleMap = useXYChartColorScales();
  const { truncationMode } = useXYChartTruncationMode();
  const formatCategory = useFormatCategory();
  const { filteredSeriesWithDatapoints } = useBarData();

  const ctx = {
    seriesToMetadata,
    axisIdToFormatter,
    axisIdToScaleDomain,
    datapointMetadataMap,
    formatCategory,
    intl,
    colorScaleMap,
    allBarSeries: filteredSeriesWithDatapoints,
  };

  const { xAxisValue, xAxisDuration, label } = buildSharedTooltipHeader(
    preferred,
    ctx,
  );

  const rows: LazySharedRow[] = datapoints.map((result) => ({
    name: safeGet(seriesToMetadata, result.series).name,
    result,
  }));

  return (
    <OverlayTooltip truncation={truncationMode}>
      <OverlayTooltip.Header
        header1={xAxisValue}
        duration1={xAxisDuration}
        label={label}
      />
      <OverlayTooltip.Series
        data={rows}
        variant="shared"
        activeItem={-1}
        seriesItemTemplate={(item) => (
          <LazySharedTooltipRow raw={item} ctx={ctx} />
        )}
      />
    </OverlayTooltip>
  );
}
